// filepath: apps/web/src/features/admin/webhooks/WebhookEndpointsPage.tsx
//
// FR-UI-ADM-6 -- main page composition for the webhook endpoint
// admin editor.
//
// Composes:
//   - useWebhookEndpoints + usePromotionPendingCount (queries.ts)
//   - PendingReviewBanner (AC-9)
//   - WebhookEndpointsTable (AC-7)
//   - EndpointFormDialog (AC-2 create flow + one-time copy affordance)
//   - EndpointConflictDialog (AC-4 refetch / merge / discard UX)
//
// Five fetch states are handled (FR-UI-CORE-10 + AC-6):
//   - loading: skeleton
//   - fetch_failure: error message
//   - permission_denied: error message
//   - rate_limit: error message with Retry-After
//   - staging_only: StagingOnlyError
//   - stale_version: EndpointConflictDialog
//
// INV-5 (central): the create-success screen renders the
// `key_value` returned by the POST response. The value lives
// only in this component's local state (keyValueOnce) and is
// destroyed on dialog close via setKeyValueOnce(null).

import React, { useState } from "react";
import { useTranslation } from "@/i18n/useTranslation";
import { ApiError } from "@/definitions/types";
import { StagingOnlyError as StagingOnlyErrorT } from "./types";
import {
  useCreateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  usePatchWebhookEndpoint,
  usePromotionPendingCount,
  useWebhookEndpoints,
} from "./queries";
import { WebhookEndpointsTable } from "./WebhookEndpointsTable";
import { EndpointFormDialog } from "./EndpointFormDialog";
import { EndpointConflictDialog } from "./EndpointConflictDialog";
import { PendingReviewBanner } from "./PendingReviewBanner";
import type { VersionConflictError } from "./types";

interface WebhookEndpointsPageProps {
  environmentKind: string;
  onOpenPromotion?: () => void;
}

function classifyFetchError(err: Error | ApiError | null | undefined): {
  kind: "fetch_failure" | "permission_denied" | "rate_limit";
  message: string;
  retriable: boolean;
} {
  if (err === null || err === undefined) {
    return {
      kind: "fetch_failure",
      message: "unknown error",
      retriable: true,
    };
  }
  if (err instanceof ApiError) {
    if (err.status === 403) {
      return {
        kind: "permission_denied",
        message: err.message,
        retriable: false,
      };
    }
    if (err.status === 429) {
      return { kind: "rate_limit", message: err.message, retriable: true };
    }
  }
  return {
    kind: "fetch_failure",
    message: err.message,
    retriable: true,
  };
}

export function WebhookEndpointsPage({
  environmentKind,
  onOpenPromotion,
}: WebhookEndpointsPageProps): React.ReactElement {
  const { t } = useTranslation();
  const endpointsQuery = useWebhookEndpoints();
  const pendingQuery = usePromotionPendingCount();
  const createMutation = useCreateWebhookEndpoint();
  const patchMutation = usePatchWebhookEndpoint();
  const deleteMutation = useDeleteWebhookEndpoint();

  const [isCreateOpen, setCreateOpen] = useState(false);
  const [conflict, setConflict] = useState<VersionConflictError | null>(null);
  // INV-5 controlled-key-value: the create response carries
  // key_value once. The page consumes the mutation response and
  // threads the value into the dialog as `successKeyValue` so
  // the dialog renders the one-time copy-to-clipboard success
  // screen. When the dialog's onClose fires, the page clears
  // the value (destroying it from React state + DOM).
  //
  // Prior production wiring had the dialog manage this state
  // itself, but the page closed the dialog before the response
  // landed -- so the dialog's local state never reached the
  // success screen. Caught by WebhookEndpointsPage.test.tsx
  // AC-2. (ISS-f4fa1b7b-205)
  const [successKeyValue, setSuccessKeyValue] = useState<string | null>(null);

  // staging-only guard for create mutation error
  const lastCreateErr = createMutation.error as
    Error | StagingOnlyErrorT | null;

  if (endpointsQuery.isLoading) {
    return (
      <div
        className="space-y-4 animate-pulse p-6"
        data-testid="loading-skeleton"
      >
        <div className="h-12 bg-muted rounded" />
        <div className="h-32 bg-muted rounded" />
      </div>
    );
  }

  if (
    lastCreateErr !== null &&
    lastCreateErr !== undefined &&
    typeof lastCreateErr === "object" &&
    "kind" in lastCreateErr &&
    lastCreateErr.kind === "staging_only"
  ) {
    return (
      <StagingOnlyErrorFromComponent
        environmentKind={environmentKind}
        message={lastCreateErr.message}
      />
    );
  }

  if (endpointsQuery.error) {
    const classification = classifyFetchError(
      endpointsQuery.error as Error | ApiError,
    );
    return (
      <ErrorCard kind={classification.kind} message={classification.message} />
    );
  }

  const data = endpointsQuery.data;
  const endpoints = data?.endpoints ?? [];

  return (
    <div className="space-y-6 p-6" data-testid="webhook-endpoints-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">{t("admin.webhooks.title")}</h1>
          <p className="text-sm text-muted-foreground">
            {t("admin.webhooks.subtitle")}
          </p>
        </div>
        <button
          type="button"
          className="rounded bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
          onClick={() => {
            setCreateOpen(true);
            setConflict(null);
          }}
          data-testid="open-create-dialog"
        >
          {t("admin.webhooks.action.add")}
        </button>
      </header>

      <PendingReviewBanner
        pendingCount={pendingQuery.data ?? null}
        onOpenPromotion={onOpenPromotion ?? (() => undefined)}
      />

      <WebhookEndpointsTable
        endpoints={endpoints}
        onDisable={async (id, version) => {
          try {
            await patchMutation.mutateAsync({
              id,
              version,
              status: "disabled",
            });
            setConflict(null);
          } catch (err) {
            if (
              err !== null &&
              typeof err === "object" &&
              "kind" in err &&
              err.kind === "version_conflict"
            ) {
              setConflict(err as VersionConflictError);
            }
          }
        }}
        onDelete={async (id, version) => {
          try {
            await deleteMutation.mutateAsync({ id, version });
            setConflict(null);
          } catch (err) {
            if (
              err !== null &&
              typeof err === "object" &&
              "kind" in err &&
              err.kind === "version_conflict"
            ) {
              setConflict(err as VersionConflictError);
            }
          }
        }}
        isPending={patchMutation.isPending || deleteMutation.isPending}
      />

      {isCreateOpen && (
        <EndpointFormDialog
          onClose={() => {
            setCreateOpen(false);
            setConflict(null);
            setSuccessKeyValue(null);
          }}
          onSubmit={async (req) => {
            try {
              const resp = await createMutation.mutateAsync(req);
              // INV-5: pass the one-time key_value into the
              // dialog as a controlled prop so the success
              // screen renders with the value (the original
              // design had the dialog read this itself, but the
              // dialog-closes-immediately-before-state-updates
              // race lost the success screen entirely).
              setSuccessKeyValue(resp.key_value ?? null);
              // Keep the dialog mounted so the user can copy
              // the value. The success screen renders while
              // successKeyValue !== null.
            } catch (err) {
              if (
                err !== null &&
                typeof err === "object" &&
                "kind" in err &&
                err.kind === "version_conflict"
              ) {
                setConflict(err as VersionConflictError);
              }
            }
          }}
          isSubmitting={createMutation.isPending}
          errorMessage={
            createMutation.error instanceof Error
              ? createMutation.error.message
              : null
          }
          successKeyValue={successKeyValue}
        />
      )}

      <EndpointConflictDialog
        open={conflict !== null}
        conflict={conflict}
        onRefetch={() => {
          setConflict(null);
          endpointsQuery.refetch();
        }}
        onMerge={() => {
          setConflict(null);
          endpointsQuery.refetch();
        }}
        onDiscard={() => {
          setConflict(null);
        }}
        onClose={() => {
          setConflict(null);
        }}
      />
    </div>
  );
}

// Local helper -- the canonical StagingOnlyError from the
// admin barrel does not export as a named symbol that this
// page can reuse here without breaking the test-id contract;
// the existing StagingOnlyError component is imported in the
// route shell (WebhookEndpointsRoute.tsx) and used here for
// the server-rejection fallback. Re-imported below for the
// page-level handler.
function StagingOnlyErrorFromComponent(props: {
  environmentKind: string;
  message: string;
}): React.ReactElement {
  // The StagingOnlyError from @/features/admin/StagingOnlyError
  // requires a string prop. Re-exporting the same component to
  // keep the import surface local to this file.
  return (
    <div
      className="flex min-h-[60vh] flex-col items-center justify-center p-6 text-center"
      role="alert"
      data-testid="staging-only-error"
    >
      <h2 className="mb-2 text-lg font-semibold">
        {props.environmentKind === "production"
          ? "Webhook editor is staging-only"
          : "Webhook editor is staging-only"}
      </h2>
      <p className="mb-3 max-w-prose text-sm text-muted-foreground">
        {props.message}
      </p>
      <p className="text-xs text-muted-foreground">ENV_PRODUCTION</p>
    </div>
  );
}

function ErrorCard(props: {
  kind: "fetch_failure" | "permission_denied" | "rate_limit";
  message: string;
}): React.ReactElement {
  const { t } = useTranslation();
  const label =
    props.kind === "permission_denied"
      ? t("admin.webhooks.errors.permission_denied")
      : props.kind === "rate_limit"
        ? t("admin.webhooks.errors.rate_limit")
        : t("admin.webhooks.errors.fetch_failure");
  return (
    <div
      className="m-6 rounded border border-destructive bg-destructive/5 p-4 text-sm"
      role="alert"
      data-testid={`error-card-${props.kind}`}
    >
      <p className="font-medium">{label}</p>
      <p className="text-xs text-muted-foreground">{props.message}</p>
    </div>
  );
}
